import { breadcrumbsIntegration, captureException, captureMessage, init as SentryInit } from '@sentry/browser'

import { HttpErrorResponse, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import { ChangeDetectorRef, ErrorHandler, Injectable, NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { FormField, provideSignalFormsConfig } from '@angular/forms/signals'
import { NG_STATUS_CLASSES } from '@angular/forms/signals/compat'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { routing } from './app.routing'

import { environment } from '../environments/environment'

// Fileuploads
import { A11yModule } from '@angular/cdk/a11y'
import { OverlayModule } from '@angular/cdk/overlay'
import { ScrollingModule } from '@angular/cdk/scrolling'
import { CdkTableModule } from '@angular/cdk/table'
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatChipsModule } from '@angular/material/chips'
import { DateAdapter, MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatDialogModule } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatListModule } from '@angular/material/list'
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { MatSortHeaderIntl, MatSortModule, SortDirection } from '@angular/material/sort'
import { MatStepperIntl, MatStepperModule } from '@angular/material/stepper'
import { MatTableModule } from '@angular/material/table'
import { MatTabsModule } from '@angular/material/tabs'
import { MatTooltipModule } from '@angular/material/tooltip'

// File drop
import { FileDropContentTemplateDirective } from './_jaettu-angular/file-drop/file-drop-templates.directive'
import { FileDropComponent } from './_jaettu-angular/file-drop/file-drop.component'

// Material
const globalRippleConfig: RippleGlobalOptions = { disabled: true }

import { LemonDateAdapter } from './_jaettu-angular/_material/LemonDateAdapter'

// Pipes
import { BigCurrencyPipe, BigCurrencyWithLocalePipe, CurrencyPipe, CurrencyWithLocalePipe } from './_jaettu-angular/_pipes/lemon-currency.pipe'
import {
  LemonDateAndTimePipe,
  LemonDatePipe,
  LemonDateWithLocalePipe,
  LemonLocalDatePipe,
  LemonLocalDateWithLocalePipe,
  LemonLocalToMonthAndYearPipe,
  LemonNumberDateAndTimePipe,
  LemonNumberDatePipe,
  LemonNumberDateWithLocaleNarrowPipe,
  LemonNumberDateWithLocalePipe,
  LemonNumberMonthToMonthAndYearPipe,
  LemonTimestampDateAndTimePipe,
  LemonTimestampDateAndTimeWithLocalePipe,
  LemonTimestampPipe,
  LemonTimestampWithLocalePipe,
} from './_jaettu-angular/_pipes/lemon-date.pipe'
import { BigDecimalPipe, BigDecimalWithLocalePipe, DecimalPipe, DecimalWithLocalePipe } from './_jaettu-angular/_pipes/lemon-decimal.pipe'
import { PrettyPrintJsonPipe } from './_jaettu-angular/_pipes/lemon-json.pipe'
import { LemonMaaPipe, LemonMaaWithlocalePipe } from './_jaettu-angular/_pipes/lemon-maa.pipe'
import { LemonKaannaPipe, LemonKaannaWithLocalePipe } from './_jaettu-angular/_pipes/lemon-translate.pipe'
// import { AuthImagePipe } from './_angular/_pipes/auth-image-pipe'

// Directives
import { CountToDirective } from './_jaettu-angular/_directives/count-to.directive'
import { MonthPickerDirective } from './_jaettu-angular/_directives/month-picker.directive'
import { NumberDatePickerDirective } from './_jaettu-angular/_directives/number-date-picker.directive'
import { LemonNumberOnlyDirective } from './_jaettu-angular/_directives/number-only.directive'
import { PlaceholderWithRequiredIndicatorDirective } from './_jaettu-angular/_directives/placeholder-with-required-indicator.directive'
import { TimeFormatDirective } from './_jaettu-angular/_directives/time-format.directive'

// Components
import { BarcodeComponent } from './_jaettu-angular/_components/barcode.component'
import { LoadingContentComponent } from './_jaettu-angular/_components/loading-content.component'
import { LoadingComponent } from './_jaettu-angular/_components/loading.component'
import { QrcodeComponent } from './_jaettu-angular/_components/qrcode.component'
// import { LemonHenkiloYritysValitsin } from './_jaettu-angular/_components/valinta-component'
import { LemonDialogHeader } from './lemon-dialog-header.component'

// Banks, Tink
// import { TinkOauthCallbackComponent } from './banks/tink/tink-oauth-callback.component'
// import { TinkNaytaDataComponent } from './banks/tink/tink-nayta-data.component'

// Banks, Nordic Api Gateway
import { ViiaOauthCallbackComponent } from './banks/viia/viia-oauth-callback.component'

// Banks, Holvi
import { HolviOauthCallbackComponent } from './banks/holvi/holvi-oauth-callback.component'

// Login, Forgot password, Reset password, home
import { BrowserClockCheckComponent } from './_jaettu-angular/_components/browser-clock-check.component'
import { AppMenuComponent } from './app-menu.component'
import { AppComponent } from './app.component'
import { DefaultRedirectComponent } from './default-redirect.component'
import { KirjanpidonRaportitBruttotuloslaskelmaComponent } from './kirjanpidon-raportit/bruttotuloslaskelma.component'
import { KirjanpidonRaportitComponent } from './kirjanpidon-raportit/kirjanpidon-raportit.component'
import { KirjanpidonRaportitPaakirjaComponent } from './kirjanpidon-raportit/paakirja.component'
import { KirjanpidonRaportitTaseComponent } from './kirjanpidon-raportit/tase.component'
import { KirjanpidonRaportitTuloslaskelmaComponent } from './kirjanpidon-raportit/tuloslaskelma.component'
import { KirjanpidonRaportitVirallinenTaseComponent } from './kirjanpidon-raportit/virallinen-tase.component'
import { KirjanpidonRaportitVirallinenTuloslaskelmaComponent } from './kirjanpidon-raportit/virallinen-tulos.component'
import { AddNumberComponent } from './login/add-number.component'
import { ChangeComponent } from './login/change.component'
import { ForgotComponent } from './login/forgot.component'
import { LoginComponent } from './login/login.component'
import { NoRightsComponent } from './login/no-rights.component'
import { NoRolesComponent } from './login/no-roles.component'
import { NoSettingsComponent } from './login/no-settings.component'
import { ResetComponent } from './login/reset.component'
import { SecondFactorComponent } from './login/second-factor.component'
import { PoytakirjaSignedDialog } from './reports/poytakirja-signed.dialog'
import { TilinpaatoksetComponent } from './reports/tilinpaatokset.component'
import { TilinpaatosSignedDialog } from './reports/tilinpaatos-signed.dialog'
import { TilinpaatosComponent } from './reports/tilinpaatos.component'
import { YhtiokokouksenPoytakirjaComponent } from './reports/yhtiokokouksen-poytakirja.component'
import { AsiakkaanPalvelukokonaisuusComponent } from './sopimukset/palvelukokonaisuus.component'
import { SopimusJaEhdotComponent } from './sopimukset/sopimus-ja-ehdot.component'
import { TervetuloaUusiKayttajaComponent } from './tervetuloa/tervetuloa-uusi-kayttaja.component'
import { UnsupportedBrowser } from './unsupported-browser.component'

import { TunnistautuminenNetsAineistonLatausComponent } from './tunnistautuminen/nets/aineiston-lataus-tunnistautuminen-pankit.component'
import { TunnistautuminenNetsMaksujenVahvistusComponent } from './tunnistautuminen/nets/maksujen-vahvistus-pankit.component'
import { TunnistautuminenNetsReturnComponent } from './tunnistautuminen/nets/nets-return.component'
import { TunnistautuminenNetsPankitComponent } from './tunnistautuminen/nets/pankit.component'
import { TunnistautuminenNetsDialog } from './tunnistautuminen/nets/tunnistautuminen-nets.dialog'

import { SopimusTauollaComponent } from './sopimukset/sopimus-tauolla.component'

// Dialogit
import { AreYouSureDialog } from './_jaettu-angular/_components/are-you-sure.dialog'
import { InfoDialog } from './_jaettu-angular/_components/info.dialog'
import { VirheDialog } from './_jaettu-angular/_components/virhe.dialog'
import { VaihdaKieliDialog } from './vaihda-kieli.dialog'
import { VaihdaYritysDialog } from './vaihda-yritys.dialog'
import { VaihdaSalasanaDialog } from './vaihda-salasana.dialog'
// import { UudetKayttoehdotDialog } from './kayttoehdot/uudet-kayttoehdot.dialog'
import { AsiakasKyselyDialog } from './asetukset/asiakaskysely.dialog'
import { EnableBankingLoginCompletedDialog } from './banks/enable-banking/enable-banking-login-completed.dialog'
import { EnableBankingLoginFailedDialog } from './banks/enable-banking/enable-banking-login-failed.dialog'
import { EnableBankingLoginRequiredDialog } from './banks/enable-banking/enable-banking-login-required.dialog'
import { GeneralBankSelectionDialog } from './banks/general-bank-selection.dialog'
import { HolviLoginCompletedDialog } from './banks/holvi/holvi-login-completed.dialog'
import { HolviLoginFailedDialog } from './banks/holvi/holvi-login-failed.dialog'
import { HolviLoginRequiredDialog } from './banks/holvi/holvi-login-required.dialog'
import { ViiaLoginCompletedDialog } from './banks/viia/viia-login-completed.dialog'
import { ViiaLoginFailedDialog } from './banks/viia/viia-login-failed.dialog'
import { ViiaLoginRequiredDialog } from './banks/viia/viia-login-required.dialog'
import { MaksutMainosLaskujenVastaanottoDialog } from './maksut/maksut-mainos-laskujen-vastaanotto.dialog'
import { NaytaTekstiaDialog } from './nayta-tekstia.dialog'
import { TilinpaatosAreYouSureDialog } from './reports/tilinpaatos-are-you-sure.dialog'
import { MuutoksiaPalveluunDialog } from './sopimukset/muutoksia-palveluun.dialog'
// import { UusiSopimusDialog } from './sopimukset/uusi-sopimus.dialog'
import { PepTekstiSharedDialog } from './tervetuloa/pep-teksti-shared.dialog'
import { TervetuloaSopimusV2Dialog } from './tervetuloa/tervetuloa-sopimus-v2.dialog'
// import { TervetuloaSopimusDialog } from './tervetuloa/tervetuloa-sopimus.dialog'
import { UudetTuntemistiedotDialog } from './tervetuloa/uudet-tuntemistiedot.dialog'

// Services, vain tämä projekti
import { AiiaDialogService } from './_angular/service/aiia-dialog.service'
import { AllowedDialogsService } from './_angular/service/allowed-dialogs.service'
import { AsetustenOletuksetService } from './_angular/service/asetusten-oletukset.service'
import { LemonBarcodeDetector } from './_angular/service/barcode/barcode-detector.service'
import { EnableBankingDialogService } from './_angular/service/enable-banking-dialog.service'
import { HolviDialogService } from './_angular/service/holvi-dialog.service'
import { KayttajaService, LaskuttavaAsiakasLemonaidProvider } from './_angular/service/kayttaja.service'
import { LaskuSummatService } from './_angular/service/lasku/lasku-summat.service'
import { LaskuService } from './_angular/service/lasku/lasku.service'
import { LaskutusprofiiliService } from './_angular/service/lasku/laskutusprofiili.service'
import { LaskutusHakuStateService } from './_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { AsiakasDialogiService } from './laskut/asiakkaat/asiakas-dialogi.service'
import { LaskunAsiakkaanValidointiService } from './laskut/lasku-asiakkaan-validointi.service'
import { LaskunAsiakasTypeahead, LaskunToimittajaTypeahead, LaskunTuoteTypeahead } from './_angular/service/lasku/lasku.typeahead'
import { LemonHttpService } from './_angular/service/lemon-http.service'
import { ProjektiServiceLemonaidImpl } from './_angular/service/lemonaid-projektit.service'
import { LoginService } from './_angular/service/login.service'
import { MuutoksiaPalveluunDialogService } from './_angular/service/muutoksia-palveluun-dialog.service'
import { TilinpaatosLemonaidUsersService } from './_angular/service/tilinpaatos-lemonaid-users.service'
import { TunnistautuminenService } from './_angular/service/tunnistautuminen.service'
import { TuntemistiedotDialogService } from './_angular/service/tuntemistiedot-dialog.service'
import { UserSettingsService } from './_angular/service/user-settings.service'
import { VersionTarkistusPalvelu } from './_angular/service/version-tarkistus.palvelu'
import { ReportImagesService } from './reports/report-images.service'

// Logs
import { TrackingServiceLemonaidImpl } from './_angular/service/logs/tracking.service'
import { TrackingService } from './_jaettu-angular/tracking'

// Services, jaettu angular projektien kesken
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { AlvService } from './_jaettu-angular/service/alv.service'
import { DragAndDropService } from './_jaettu-angular/service/drag-and-drop.service'
import { FileSaverService } from './_jaettu-angular/service/file-saver'
import { FormValidationService } from './_jaettu-angular/service/form-validation.service'
import { LadataanService } from './_jaettu-angular/service/ladataan.service'
import { LaskuttavaAsiakasProvider } from './_jaettu-angular/service/lasku/laskuttava-asiakas.service'
import { LemonTranslationService } from './_jaettu-angular/service/lemon-translation.service'
import { LocalStorageService } from './_jaettu-angular/service/local-storage.service'
import { MaaService } from './_jaettu-angular/service/maa.service'
import { TiedostojenLataamisService } from './_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from './_jaettu-angular/service/timestamp-service'
import { VasenValikkoService } from './_jaettu-angular/service/vasen-valikko.service'
import { WindowSizeService } from './_jaettu-angular/service/window.service'

// Shared
import { BankBalancePublicUriService } from '@lemon/core-firebase/service/bank-balance-public-uri.service'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { FirestoreIndeksoija } from '@lemon/core-firebase/service/firestore.indeksoija'
import { KayttoehdotUriService } from '@lemon/core-firebase/service/kayttoehdot-uri.service'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { KycUriService } from '@lemon/core-firebase/service/kyc-uri.service'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { LemonaidRaportitUriService } from '@lemon/core-firebase/service/lemonaid-raportit-uri.service'
import { LemonaidProjektitUriService } from '@lemon/core-firebase/service/projektit-uri.service'
import { TilinpaatosUriService } from '@lemon/core-firebase/service/tilinpaatos-uri.service'
import { CountryLocalizationService, TranslationService } from '@lemon/core-firebase/service/translation.service'
import { VatNumberValidationService } from '@lemon/core-firebase/service/vat-number-validation.service'
import { BicService } from '@lemon/core/service/bic.service'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { ProjektitService } from './_jaettu-angular/projektit.service'

// Shared, Lasku
import { LaskuKorkoService } from '@lemon/core-firebase/service/lasku/lasku-korko.service'
import { LaskuLahetyksenToistoService } from '@lemon/core-firebase/service/lasku/lasku-lahetyksen-toisto.service'
import { LaskuSharedService, ReskontraService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuSpostiService } from '@lemon/core-firebase/service/lasku/lasku-sposti.service'
import { YksikkoService } from '@lemon/core-firebase/service/lasku/yksikko.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { LaskuIndeksoija } from '@lemon/core-firebase/service/lasku/lasku.indeksoija'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { SahkoisenLaskunValittajaService } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'

// Palkkausfi
import { PalkkausfiComponent, PalkkausFiService } from './palkkausfi/palkkausfi.component'

// Maksut
import { MaksuKatseleComponentDataResolve, MaksuLuoUusiComponentDataResolve, MaksuMuokkaaComponentDataResolve, MaksuToimittajaDataResolve } from './_angular/_resolvers/maksu.resolve'
import { MaksutAsetuksetComponent } from './maksut/maksut-asetukset.component'
import { MaksutKatseleComponent } from './maksut/maksut-katsele.component'
import { MaksutLopetaVastaanottoDialog } from './maksut/maksut-lopeta-vastaanotto.dialog'
import { MaksutMainosComponent } from './maksut/maksut-mainos.component'
import { MaksutMaksupaivaDialog } from './maksut/maksut-maksupaiva.dialog'
import { MaksutMaksutapaDialog } from './maksut/maksut-maksutapa.dialog'
import { MaksutMaksutavatComponent } from './maksut/maksut-maksutavat.component'
import { MaksutSharedStateService } from './maksut/maksut-shared-state.service'
import { MaksutUusiComponent } from './maksut/maksut-uusi.component'
import { MaksutVahvistaComponent } from './maksut/maksut-vahvista.component'
import { MaksutVastaanottoAloitettuDialog } from './maksut/maksut-vastaanotto-aloitettu.dialog'
import { MaksutComponent } from './maksut/maksut.component'

import { MaksutToimittajaPoistoVarmistusDialog } from './maksut/maksut-toimittaja-poisto-varmistus.dialog'
import { MaksutToimittajaComponent } from './maksut/maksut-toimittaja.component'
import { MaksutToimittajatComponent } from './maksut/maksut-toimittajat.component'
import { MaksutToimittajatFirestoreDataSource } from './maksut/maksut-toimittajat.firestore.datasource'

// Laskut
import { LaskuAsiakkaanTiedotComponent } from './_jaettu-angular/laskut/muokkaa/lasku-asiakkaan-tiedot.component'
import { LaskuAsiakasComponent } from './laskut/lasku-asiakas.component'
import { LaskuMuutTiedotComponent } from './_jaettu-angular/laskut/muokkaa/lasku-muut-tiedot.component'
import { LaskuYlaosanDropparitComponent } from './_jaettu-angular/laskut/muokkaa/lasku-ylaosan-dropparit.component'
import { EmailPohjaComponent } from './laskut/asetukset/emailpohja.component'
import { LaskuasetuksetComponent } from './laskut/asetukset/laskuasetukset.component'
import { PerusasetuksetComponent } from './laskut/asetukset/perusasetukset.component'
import { ReskontraAsetuksetComponent } from './laskut/asetukset/reskontra-asetukset.component'
import { AsiakasComponent } from './laskut/asiakkaat/asiakas.component'
import { AsiakkaatComponent } from './laskut/asiakkaat/asiakkaat.component'
import { LaskuMuokkausEstettyDialog } from './laskut/dialogit/lasku.muokkaus-estetty.dialog'
import { LahetaComponent } from './laskut/laheta.component'
import { LaskuRiviComponent } from './laskut/lasku-rivi.component'
import { KuutioIkoniComponent } from './laskut/kuutio-ikoni.component'
import { EuKuluttajamyyntiHuomautusComponent } from './laskut/eu-kuluttajamyynti-huomautus.component'
import { EuKuluttajamyyntiDialog } from './laskut/dialogit/eu-kuluttajamyynti.dialog'
import { YksikkoHakuComponent } from './laskut/yksikko-haku.component'
import { YtjHakuComponent } from './laskut/dialogit/ytj-haku.component'
import { LaskuRivitComponent } from './laskut/lasku-rivit.component'
import { UusiLaskuriviHakuComponent } from './laskut/uusi-laskurivi-haku.component'
import { LaskuComponent } from './laskut/lasku.component'
import { LaskuKatseleMuokattavaComponent } from './laskut/lasku.katsele.component'
import { LaskuTeeIndeksiUudelleenComponent } from './laskut/lasku.tee-indeksi-uudelleen.component'
import { LaskuTeePdfUudelleenComponent } from './laskut/lasku.tee-pdf-uudelleen.component'
import { LaskuTeeTilaJaIndeksiUudelleenComponent } from './laskut/lasku.tee-tila-ja-indeksi-uudelleen.component'
import { AsiakasrekisteriHakuComponent } from './laskut/asiakkaat/asiakasrekisteri-haku/asiakasrekisteri-haku.component'
import { LaskuHakuComponent } from './laskut/lasku-haku/lasku-haku.component'
import { TuoterekisteriHakuComponent } from './laskut/tuotteet/tuoterekisteri-haku/tuoterekisteri-haku.component'
import { LaskutComponent } from './laskut/laskut.component'
import { ToistuvatlaskutComponent } from './laskut/toistuvat-laskut.component'
import { TuoteComponent } from './laskut/tuotteet/tuote.component'
import { TuoteDialog } from './laskut/tuotteet/tuote.dialog'
import { TuoteDialogiService } from './laskut/tuotteet/tuote-dialogi.service'
import { TuotteetComponent } from './laskut/tuotteet/tuotteet.component'

// Laskujen dialogit
import { AsiakasPoistoVarmistusDialog } from './laskut/asiakkaat/asiakas-poisto-varmistus.dialog'
import { LaskuAjastaDialog } from './laskut/dialogit/lasku-ajasta.dialog'
import { LaskuAsiakasDialog } from './laskut/dialogit/lasku-asiakas.dialog'
import { LaskuKommenttiDialog } from './laskut/dialogit/lasku-kommentti.dialog'
import { LaskuMaksumuistutusSpostiDialog } from './laskut/dialogit/lasku-maksumuistutus.sposti.dialog'
import { LaskuPerintaPeruutusAreYouSureDialog } from './laskut/dialogit/lasku-perinta-peruutus-are-you-sure.dialog'
import { LaskuPerintaSpostiDialog } from './laskut/dialogit/lasku-perinta.sposti.dialog'
import { LaskuTaydennaAsetuksetDialog } from './laskut/dialogit/lasku-taydenna-asetukset.dialog'
import { LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialog } from './laskut/dialogit/lasku.haluatko-varmasti-perua-maksetuksi-merkitsemisen.dialog'
import { LaskuKokoVirheDialog } from './laskut/dialogit/lasku.koko-virhe.dialog'
import { LaskuLataaDialog } from './laskut/dialogit/lasku.lataa.dialog'
import { LaskuMerkitseOsittainMaksetuksiDialog } from './laskut/dialogit/lasku.merkitse-osittain-maksetuksi.dialog'
import { LaskuMuokkaaTulostettuaDialog } from './laskut/dialogit/lasku.muokkaa-tulostettua.dialog'
import { LaskuReskontraTiedotDialog } from './laskut/dialogit/lasku.reskontra-tiedot.dialog'
import { TuotePoistoVarmistusDialog } from './laskut/tuotteet/tuote-poisto-varmistus.dialog'

// Dialog
import { DialogHeaderWithHelpComponent } from './_jaettu-angular/component/dialog-header-with-help/dialog-header-with-help.component'

// Laskujen esikatselut
import { LaskuEmailEsikatseluPerinteinen } from './_jaettu-angular/laskut/esikatselu/email.perinteinen.component'
import { LaskuPdfEsikatseluPerinteinen } from './_jaettu-angular/laskut/esikatselu/pdf.perinteinen.component'

// Laskujen datasourcet
import { AsiakkaatFirestoreDataSource } from './laskut/asiakkaat/asiakkaat.firestore.datasource'
import { LaskuHakuDataSourceLemonaid } from './laskut/lasku-haku/lasku-haku-lemonaid.datasource'
import { LaskutFirestoreDataSource } from './laskut/laskut.firestore.datasource'
import { TuotteetFirestoreDataSource } from './laskut/tuotteet/tuotteet.firestore.datasource'

// Tositteet, jaettu
import { TositeKatseleComponent } from './tositteet/katsele/tosite.katsele.component'
import { TositeKuvaCacheService } from './tositteet/kuvat/tosite-kuva-cache.service'
import { TositeKuvaComponent } from './tositteet/kuvat/tosite-kuva.component'
import { TiedostonSivutComponent } from './tositteet/kuvat/tiedoston-sivut.component'
import { LisaaTositteitaComponent } from './tositteet/lisaa-tositteita/lisaa-tositteita.component'
import { MaksutapaValintaComponent } from './tositteet/lisaa-tositteita/maksutapa-valinta/maksutapa-valinta.component'
import { MaksukortitComponent } from './tositteet/maksukortit/maksukortit.component'
import { MaksukorttiLisaaDialog } from './tositteet/maksukortit/maksukortti-lisaa.dialog'
import { MaksukorttiTunnistettuDialog } from './tositteet/maksukortit/maksukortti-tunnistettu.dialog'
import { MaksukorttiService } from './tositteet/maksukortit/maksukortti.service'
import { OcrLuentaComponent } from './tositteet/lisaa-tositteita/ocr-luenta/ocr-luenta.component'
import { ProjektiKenttaComponent } from './tositteet/lisaa-tositteita/projekti-kentta/projekti-kentta.component'
import { ToimittajaKenttaComponent } from './tositteet/lisaa-tositteita/toimittaja-kentta/toimittaja-kentta.component'
import { TositeKorttiComponent } from './tositteet/lisaa-tositteita/tosite-kortti/tosite-kortti.component'
import { TositePudotusalueComponent } from './tositteet/lisaa-tositteita/tosite-pudotusalue/tosite-pudotusalue.component'
import { TositeLuoUusiComponent } from './tositteet/luo-uusi/tosite.luo-uusi.component'
import { MuokkaaTositettaComponent } from './tositteet/muokkaa-tositetta-uusi/muokkaa-tositetta.component'
import { TositteenMuokkausComponent } from './tositteet/muokkaus/tositteen.muokkaus.component'
import { TositteenMuokkausPdfImageBox } from './tositteet/muokkaus/tositteen.muokkaus.pdf-image-box.component'
import { TositePoistaDialog } from './tositteet/poista-dialog/tosite.poista.dialog'

import { KuukausiValintaComponent } from './tositteet/uusi-listaus/kuukausi-valinta/kuukausi-valinta.component'
import { SelvitysKorttiComponent } from './tositteet/uusi-listaus/selvitys-kortti/selvitys-kortti.component'
import { TositeHakuComponent } from './tositteet/uusi-listaus/tosite-haku/tosite-haku.component'
import { TositeListaComponent } from './tositteet/uusi-listaus/tosite-lista/tosite-lista.component'
import { TositeUusiListausComponent } from './tositteet/uusi-listaus/tosite-uusi-listaus.component'

// Tositteet, osto
import { OstotositeListausComponent } from './tositteet/osto/ostotosite.listaus.component'
// import { OstotositeKatseleComponent } from './tositteet/osto/ostotosite.katsele.component'
// import { OstotositeMuokkaaComponent } from './tositteet/osto/ostotosite.muokkaa.component'
// import { OstotositePoistaDialog } from './tositteet/osto/ostotosite.poista.dialog'
// import { OstotositeListausFirebaseDataSource } from './tositteet/osto/ostotosite.listaus.firebase.datasource'

// Tositteet, myynti
import { MyyntitositeListausComponent } from './tositteet/myynti/myyntitosite.listaus.component'
// import { MyyntitositeMuokkaaComponent } from './tositteet/myynti/myyntitosite.muokkaa.component'
// import { MyyntitositeKatseleComponent } from './tositteet/myynti/myyntitosite.katsele.component'
// import { MyyntitositePoistaDialog } from './tositteet/myynti/myyntitosite.poista.dialog'
// import { MyyntitositeListausFirebaseDataSource } from './tositteet/myynti/myyntitosite.listaus.firebase.datasource'

// Tositteet, palkka
// import { PalkkatositePoistaDialog } from './tositteet/palkka/palkkatosite.poista.dialog'
// import { PalkkatositeKatseleComponent } from './tositteet/palkka/palkkatosite.katsele.component'
// import { PalkkatositeMuokkaaComponent } from './tositteet/palkka/palkkatosite.muokkaa.component'
import { PalkkatositeListausComponent } from './tositteet/palkka/palkkatosite.listaus.component'
// import { PalkkatositeListausFirebaseDataSource } from './tositteet/palkka/palkkatosite.listaus.firebase.datasource'

// Tositteet, tiliote
// import { TiliotetositePoistaDialog } from './tositteet/tiliote/tiliotetosite.poista.dialog'
import { TiliotetositeListausComponent } from './tositteet/tiliote/tiliotetosite.listaus.component'
// import { TiliotetositeMuokkaaComponent } from './tositteet/tiliote/tiliotetosite.muokkaa.component'
// import { TiliotetositeKatseleComponent } from './tositteet/tiliote/tiliotetosite.katsele.component'
// import { TiliotetositeListausFirebaseDataSource } from './tositteet/tiliote/tiliotetosite.listaus.firebase.datasource'

// Tositteet, selvitettävät
import { SelvitettavaTositeListausComponent } from './tositteet/selvitettavat/selvitettava-tosite.listaus.component'
import { SelvitettavatMaksutapaComponent } from './tositteet/selvitettavat/selvitettavat-maksutapa.component'

// Tositteet, yhteiset
import { TositeKatseleComponentDataResolve, TositeLuoUusiComponentDataResolve, TositeMuokkaaComponentDataResolve } from './_angular/_resolvers/tosite.resolve'
import { PdfService } from './_angular/service/pdf.service'
import { MaksutavatComponent } from './tositteet/maksutavat.component'
import { TositeLukittuDialog } from './tositteet/tosite-lukittu.dialog'
import { TositteetUudistusDialog } from './tositteet/uusi-listaus/tositteet-uudistus-dialog/tositteet-uudistus.dialog'

import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { TositeIndeksoija } from '@lemon/core-firebase/service/tosite/tosite.indeksoija'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { TositeHakuStateService } from './_angular/service/tosite-haku-state.service'
import { EinvoicesAngularService } from './_angular/service/tosite/einvoices-angular.service'
import { SelvitettavatTositteetService } from './_angular/service/tosite/selvitettavat-tositteet.service'
import { TositeDatasourceUusiService } from './_angular/service/tosite/tosite-datasource-uusi.service'
import { TositeDatasourceService } from './_angular/service/tosite/tosite-datasource.service'
import { TositeKuvaService } from './_angular/service/tosite/tosite-kuva.service'
import { TositeService } from './_angular/service/tosite/tosite.service'

// Laskujen vastaanotto
import { LaskujenHyvaksyminenComponent } from './tositteet/laskujen-hyvaksyminen/laskujen-hyvaksyminen.component'
import { LaskujenHyvaksyminenEiVoiHyvaksyaEnaaDialog } from './tositteet/laskujen-hyvaksyminen/laskujen-hyvaksyminen.ei-voi-hyvaksya-enaa.dialog'
import { LaskujenVastaanottoComponent } from './tositteet/laskujen-vastaanotto/laskujen-vastaanotto.component'

// Ajopäiväkirja
import { KirjanpidonUlkopuolisetKulutComponent } from './ajopaivakirja/kirjanpidon-ulkopuoliset-kulut.component'

// Aineiston lataus
import { AineistonLatausComponent } from './aineiston-lataus/aineiston-lataus.component'

// Logit
import { LemonaidButtonComponent } from './_jaettu-angular/logs-jaettu/lemonaid-button.component'
import { LemonaidIconButtonComponent } from './_jaettu-angular/logs-jaettu/lemonaid-icon-button.component'
import { LemonaidMenuItemButtonComponent } from './_jaettu-angular/logs-jaettu/lemonaid-menu-item-button.component'
import { LemonaidPrimaryButtonComponent } from './_jaettu-angular/logs-jaettu/lemonaid-primary-button.component'
import { LemonaidSecondaryButtonComponent } from './_jaettu-angular/logs-jaettu/lemonaid-secondary-button.component'

// Guards
import { AsetuksetGuard } from './_angular/_guards/asetukset.guard'
import { AuthGuard } from './_angular/_guards/auth.guard'
import { RoleByTositeTypeGuard, RoleGuard } from './_angular/_guards/role.guard'
import { TaukoGuard } from './_angular/_guards/tauko.guard'
import { TilinpaatosUsersGuard } from './_angular/_guards/tilinpaatos-users.guard'
import { ValikkoAukiGuard, ValikkoKiinniGuard } from './_angular/_guards/valikko-kiinni.guard'

// Resolvit
import { AsiakasComponentDataResolve, LaskuComponentDataResolve, LaskuKatseleComponentDataResolve, LaskutComponentDataResolve, TuoteComponentDataResolve } from './_angular/_resolvers/lasku.resolve'

import { FirebaseLemonaid } from './_angular/service/firebase-lemonaid.service'
import { SharedFirebaseCurrentApp, SharedFirebaseLemonaid } from './_jaettu-angular/base-firebase.service'

import { AsetuksetComponent } from './asetukset/asetukset.component'
import { LaskutusprofiiliLisaaDialog } from './asetukset/laskutusprofiili-lisaa.dialog'

import { Subject } from 'rxjs'

// import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router'

import { registerLocaleData } from '@angular/common'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { MaksutService } from '@lemon/core-firebase/service/maksut.service'
import {
  AsiakasJaettuLemonaidServiceAngular,
  BankBalancePublicUriServiceAngular,
  BankConsentUriServiceAngular,
  BicAngularServiceAngular,
  CodeCheckServiceAngular,
  CurrencyServiceAngular,
  DateServiceAngular,
  FirestoreIndeksoijaAngular,
  IbanServiceAngular,
  KayttoehdotUriServiceAngular,
  KopioijaPalveluAngular,
  KycUriServiceAngular,
  LaskuIndeksoijaAngular,
  LaskuKopioijaAngular,
  LaskuKorkoServiceAngular,
  LaskuLahetyksenToistoServiceAngular,
  LaskuSharedServiceAngular,
  LaskuSpostiServiceAngular,
  YksikkoServiceAngular,
  LaskuUriServiceAngular,
  LemonaidProjektitUriServiceAngular,
  LemonaidRaportitUriServiceAngular,
  MaksutServiceAngular,
  ReskontraServiceAngular,
  SahkoisenLaskunValittajaServiceAngular,
  StringServiceAngular,
  TilinpaatosUriServiceAngular,
  ToimittajaTunnisteServiceAngular,
  TositeIndeksoijaAngular,
  TositeKopioijaAngular,
  TositeUriServiceAngular,
  TranslationServiceAngular,
  VahvistettuKorkoServiceAngular,
  VatNumberValidationServiceAngular,
  ViitenumeroServiceAngular,
} from './_jaettu-angular/angularified-services'
import { KonfiguraatioPalvelu } from './_jaettu-angular/service/lasku/configuration.service'
import { LaskunAsiakasTypeaheadBase, LaskunToimittajaTypeaheadBase, LaskunTuoteTypeaheadBase } from './_jaettu-angular/service/lasku/typeahead.service'
import { AlvNumeronTarkistajaService } from './_jaettu-angular/service/vat-number-check.service'
import { EnvironmentType } from './app.environment'

import localeEn from '@angular/common/locales/en'
import localeFi from '@angular/common/locales/fi'
import { VahvistettuKorkoService } from '@lemon/core-firebase/service/vahvistettu-korko.service'
import { PalvelukokonaisuusService } from './_jaettu-angular/service/palvelukokonaisuus.service'

registerLocaleData(localeFi, 'fi')
registerLocaleData(localeEn, 'en')

// Dynamic templates
// import { LemonDynOutput } from './_angular/components/lemon-dyn-output.component'
// import { LemonDynContent } from './_angular/components/lemon-dyn-content.component'
// import { LemonDynTrustedOutput } from './_angular/components/lemon-dyn-trusted-output.component'
// import { LemonDynLoop } from './_angular/components/lemon-dyn-loop.component'

// export class MyDateAdapter extends DateAdapter< {

// }

const isSentryEnabled = environment.airbrakeEnabled && !!window?.location?.hostname && !window.location.hostname.includes('local')

console.log('Enabled Sentry: ' + isSentryEnabled)

if (isSentryEnabled) {
  SentryInit({
    environment: environment.environment === EnvironmentType.PRODUCTION ? 'production' : 'beta',
    release: 'lemonaid@' + environment.version,
    dsn: 'https://7056cf1030bfd67c455a547aa593c0a8@o4509231716237312.ingest.de.sentry.io/4509231751757904',
    integrations: [breadcrumbsIntegration({ console: false })],
  })
}

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(err: any): void {
    // Try local logging to console
    try {
      if (console) {
        console.log(err)
      }
    } catch (e) {}

    try {
      if (isSentryEnabled) {
        const error = err.originalError || err

        if (error instanceof HttpErrorResponse) {
          this._raportoiHttpVirhe(error)
        } else if (
          Object.prototype.toString.call(error) === '[object Error]' ||
          Object.prototype.toString.call(error) === '[object ErrorEvent]' ||
          Object.prototype.toString.call(error) === '[object ProgressEvent]'
        ) {
          captureException(error)
        } else {
          captureMessage(error.error || error)
        }
      }
    } catch (error) {
      try {
        if (console) {
          console.log(err)
        }
      } catch (e) {}
    }

    try {
      // Try to avoid deep loop
      const debugCtx = err['ngDebugContext']
      const changeDetectorRef = debugCtx && debugCtx.injector.get(ChangeDetectorRef)
      if (changeDetectorRef) {
        changeDetectorRef.detach()
      }
    } catch (error) {
      try {
        if (console) {
          console.log(err)
        }
      } catch (e) {}
    }
  }

  private async _raportoiHttpVirhe(virhe: HttpErrorResponse) {
    try {
      const polku = virhe.url ? new URL(virhe.url, window.location.origin).pathname : 'tuntematon'
      const vastaus = virhe.error instanceof Blob ? await virhe.error.text() : virhe.error
      captureException(new Error(`HTTP ${virhe.status} ${polku}`), {
        tags: { httpStatus: `${virhe.status}`, httpPolku: polku },
        contexts: { httpVastaus: { status: virhe.status, statusText: virhe.statusText, vastaus } },
        fingerprint: ['http-virhe', `${virhe.status}`, polku],
      })
    } catch (error) {
      console.error('HTTP-virheen raportointi Sentryyn epäonnistui', error)
    }
  }
}

@Injectable()
export class MyMaterialMatStepperIntl implements MatStepperIntl {
  /**
   * Stream that emits whenever the labels here are changed. Use this to notify
   * components if the labels have changed after initialization.
   */
  changes: Subject<void> = new Subject()

  optionalLabel = ''
  completedLabel = ''
  editableLabel = ''

  constructor(private lemonTranslationService: LemonTranslationService) {
    lemonTranslationService.currentLanguageObservable.subscribe(kieli => {
      this.optionalLabel = this.lemonTranslationService.lokalisoiKielella('stepper.optional', kieli)
      this.completedLabel = this.lemonTranslationService.lokalisoiKielella('stepper.completed', kieli)
      this.editableLabel = this.lemonTranslationService.lokalisoiKielella('stepper.editable', kieli)
      this.changes.next()
    })
  }
}

@Injectable()
export class MyMaterialMatSortHeaderIntl implements MatSortHeaderIntl {
  /**
   * Stream that emits whenever the labels here are changed. Use this to notify
   * components if the labels have changed after initialization.
   */
  changes: Subject<void> = new Subject()

  /** ARIA label for the sorting button. */
  sortButtonLabel(id: string): string {
    return '// TODO: FIXME: IMPLEMENT ME'
  }

  /** A label to describe the current sort (visible only to screenreaders). */
  sortDescriptionLabel(id: string, direction: SortDirection): string {
    return '// TODO: FIXME: IMPLEMENT ME'
  }

  constructor(private lemonTranslationService: LemonTranslationService) {
    // lemonTranslationService.currentLanguageObservable.subscribe(kieli => {
    // this.changes.next()
    // })
  }
}

@Injectable()
export class MyMaterialPagingIntl implements MatPaginatorIntl {
  /**
   * Stream that emits whenever the labels here are changed. Use this to notify
   * components if the labels have changed after initialization.
   */
  changes: Subject<void> = new Subject()

  /** A label for the page size selector. */
  itemsPerPageLabel: string

  /** A label for the button that increments the current page. */
  nextPageLabel: string

  /** A label for the button that decrements the current page. */
  previousPageLabel: string

  /** A label for the button that moves to the first page. */
  firstPageLabel: string

  /** A label for the button that moves to the last page. */
  lastPageLabel: string

  constructor(private lemonTranslationService: LemonTranslationService) {
    lemonTranslationService.currentLanguageObservable.subscribe(kieli => {
      this.itemsPerPageLabel = this.lemonTranslationService.lokalisoiKielella('listaus.kpl-per-sivu', kieli)
      this.nextPageLabel = this.lemonTranslationService.lokalisoiKielella('listaus.seuraava-sivu', kieli)
      this.previousPageLabel = this.lemonTranslationService.lokalisoiKielella('listaus.edellinen-sivu', kieli)
      this.firstPageLabel = this.lemonTranslationService.lokalisoiKielella('listaus.ensimmainen-sivu', kieli)
      this.lastPageLabel = this.lemonTranslationService.lokalisoiKielella('listaus.viimeinen-sivu', kieli)
      this.changes.next()
    })
  }

  /** A label for the range of items within the current page and the length of the whole list. */
  getRangeLabel(page: number, pageSize: number, length: number): string {
    const lengthString = length === Number.MAX_SAFE_INTEGER ? '?' : '' + length

    if (length === 0 || pageSize === 0) {
      return '0 / ' + lengthString
    }
    length = Math.max(length, 0)
    const startIndex = page * pageSize
    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize
    return startIndex + 1 + ' - ' + endIndex + ' / ' + lengthString
  }
}

/**
 * Does not detach any subtrees. Reuses routes as long as their route config is the same.
 */
// export class LemonRouteReuseStrategy implements RouteReuseStrategy {

//   // private storepaths = ['', 'laskutus/tuotteet', 'laskutus/asiakkaat']
//   private cache = new Map<string, {route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle}>()

//   shouldDetach(route: ActivatedRouteSnapshot): boolean {
//     const key = this.key(route.routeConfig.path)
//     console.log('SHOULD DETACH "' + key + '"', route)
//     return key !== 'laskutus/tuotteet'
//   }
//   store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle): void {
//     if (route && route.routeConfig && route.routeConfig.path) {
//       const key = this.key(route.routeConfig.path)
//       // console.log('STORE "' + key + '"', detachedTree)
//       this.cache.set(key, { route: route, detachedTree: detachedTree })
//       if (key === 'laskutus/laskut/:id/kasiteltavaAvain') {
//         // console.log('store ', route.routeConfig)
//       }
//     }
//   }
//   shouldAttach(route: ActivatedRouteSnapshot): boolean {
//     const key = this.key(route.routeConfig.path)
//     const attach: boolean = this.cache.get(key) != null
//     // console.log('SHOULD ATTACH ' + attach + ' "' + key + '"')
//     return attach
//   }
//   retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle|null {
//     if (route && route.routeConfig) {
//       const key = this.key(route.routeConfig.path)
//       const fromCache = this.cache.get(key)
//       // console.log('RETRIEVE "' + key + '"', fromCache)
//       return fromCache ? fromCache.detachedTree : null
//     }
//     return null
//   }
//   shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
//     return false
//   }

//   private key(key: string): string {
//     if (key === '') {
//       return 'root'
//     }
//     return key
//   }

// }

@NgModule({
  declarations: [
    AppComponent,
    AppMenuComponent,
    DefaultRedirectComponent,
    UnsupportedBrowser,
    BrowserClockCheckComponent,

    // File drop
    FileDropComponent,
    FileDropContentTemplateDirective,

    TunnistautuminenNetsReturnComponent,
    TunnistautuminenNetsPankitComponent,
    TunnistautuminenNetsMaksujenVahvistusComponent,
    TunnistautuminenNetsAineistonLatausComponent,
    TunnistautuminenNetsDialog,
    LoginComponent,
    AddNumberComponent,
    SecondFactorComponent,
    ForgotComponent,
    ResetComponent,
    ChangeComponent,
    NoRightsComponent,
    NoSettingsComponent,
    NoRolesComponent,
    TervetuloaUusiKayttajaComponent,
    SopimusJaEhdotComponent,
    AsiakkaanPalvelukokonaisuusComponent,
    TilinpaatoksetComponent,
    TilinpaatosComponent,
    YhtiokokouksenPoytakirjaComponent,
    SopimusTauollaComponent,
    KirjanpidonRaportitComponent,
    KirjanpidonRaportitPaakirjaComponent,
    KirjanpidonRaportitTaseComponent,
    KirjanpidonRaportitTuloslaskelmaComponent,
    KirjanpidonRaportitBruttotuloslaskelmaComponent,
    KirjanpidonRaportitVirallinenTaseComponent,
    KirjanpidonRaportitVirallinenTuloslaskelmaComponent,

    // Dialogit
    LemonDialogHeader,
    AreYouSureDialog,
    LaskuTaydennaAsetuksetDialog,
    LaskuAsiakasDialog,
    LaskuKommenttiDialog,
    VaihdaKieliDialog,
    VaihdaYritysDialog,
    VaihdaSalasanaDialog,
    VirheDialog,
    InfoDialog,
    // UudetKayttoehdotDialog,
    // UusiSopimusDialog,
    MuutoksiaPalveluunDialog,
    NaytaTekstiaDialog,
    // TervetuloaSopimusDialog,
    TervetuloaSopimusV2Dialog,
    ViiaLoginRequiredDialog,
    ViiaLoginCompletedDialog,
    ViiaLoginFailedDialog,
    UudetTuntemistiedotDialog,
    PepTekstiSharedDialog,
    TilinpaatosSignedDialog,
    TilinpaatosAreYouSureDialog,
    PoytakirjaSignedDialog,
    HolviLoginRequiredDialog,
    HolviLoginCompletedDialog,
    HolviLoginFailedDialog,
    EnableBankingLoginRequiredDialog,
    EnableBankingLoginCompletedDialog,
    EnableBankingLoginFailedDialog,
    GeneralBankSelectionDialog,
    MaksutMainosLaskujenVastaanottoDialog,
    AsiakasKyselyDialog,
    // Laskudialogit
    LaskuLataaDialog,
    LaskuMuokkaaTulostettuaDialog,
    LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialog,
    LaskuKokoVirheDialog,
    // LaskuSpostiDialog,
    LaskuMaksumuistutusSpostiDialog,
    LaskuPerintaSpostiDialog,
    LaskuPerintaPeruutusAreYouSureDialog,
    LaskuMerkitseOsittainMaksetuksiDialog,
    TuotePoistoVarmistusDialog,
    AsiakasPoistoVarmistusDialog,
    LaskuReskontraTiedotDialog,
    // Palkkaus.fi
    PalkkausfiComponent,
    // Maksut
    MaksutComponent,
    MaksutVahvistaComponent,
    MaksutUusiComponent,
    MaksutKatseleComponent,
    MaksutAsetuksetComponent,
    MaksutMainosComponent,
    MaksutLopetaVastaanottoDialog,
    MaksutVastaanottoAloitettuDialog,
    MaksutMaksutapaDialog,
    MaksutMaksupaivaDialog,
    MaksutMaksutavatComponent,
    MaksutToimittajaComponent,
    MaksutToimittajatComponent,
    MaksutToimittajaPoistoVarmistusDialog,

    // Laskukomponentit
    LaskutComponent,
    LaskuHakuComponent,
    AsiakasrekisteriHakuComponent,
    TuoterekisteriHakuComponent,
    ToistuvatlaskutComponent,
    LaskuComponent,
    LahetaComponent,
    LaskuRiviComponent,
    KuutioIkoniComponent,
    EuKuluttajamyyntiHuomautusComponent,
    EuKuluttajamyyntiDialog,
    YksikkoHakuComponent,
    YtjHakuComponent,
    LaskuRivitComponent,
    UusiLaskuriviHakuComponent,
    LaskuMuokkausEstettyDialog,
    LaskuAjastaDialog,
    DialogHeaderWithHelpComponent,
    LaskuYlaosanDropparitComponent,
    LaskuAsiakkaanTiedotComponent,
    LaskuAsiakasComponent,
    LaskuMuutTiedotComponent,
    AsiakasComponent,
    AsiakkaatComponent,
    AsetuksetComponent,
    LaskutusprofiiliLisaaDialog,
    EmailPohjaComponent,
    LaskuTeePdfUudelleenComponent,
    LaskuTeeIndeksiUudelleenComponent,
    LaskuTeeTilaJaIndeksiUudelleenComponent,
    LaskuKatseleMuokattavaComponent,
    PerusasetuksetComponent,
    LaskuasetuksetComponent,
    ReskontraAsetuksetComponent,
    LaskuPdfEsikatseluPerinteinen,
    LaskuEmailEsikatseluPerinteinen,
    TuotteetComponent,
    TuoteComponent,
    TuoteDialog,
    // Yleiset
    BarcodeComponent,
    QrcodeComponent,
    LoadingComponent,
    LoadingContentComponent,
    LemonNumberOnlyDirective,
    MonthPickerDirective,
    NumberDatePickerDirective,
    CountToDirective,
    TimeFormatDirective,
    PlaceholderWithRequiredIndicatorDirective,
    // Banks, Tink
    // TinkOauthCallbackComponent,
    // TinkNaytaDataComponent,
    // Banks, Nordic Api Gateway
    ViiaOauthCallbackComponent,
    // Banks, Holvi
    HolviOauthCallbackComponent,
    // Tositteiden dialogit
    TositePoistaDialog,
    TositeLukittuDialog,
    TositteetUudistusDialog,
    // Tositteet, jaettu
    TositteenMuokkausComponent,
    MuokkaaTositettaComponent,
    TositteenMuokkausPdfImageBox,
    TositeKuvaComponent,
    TiedostonSivutComponent,
    TositeLuoUusiComponent,
    TositeKatseleComponent,
    LisaaTositteitaComponent,
    MaksutapaValintaComponent,
    MaksukortitComponent,
    MaksukorttiLisaaDialog,
    MaksukorttiTunnistettuDialog,
    OcrLuentaComponent,
    TositePudotusalueComponent,
    ProjektiKenttaComponent,
    ToimittajaKenttaComponent,
    TositeKorttiComponent,
    TositeUusiListausComponent,
    KuukausiValintaComponent,
    SelvitysKorttiComponent,
    TositeListaComponent,
    TositeHakuComponent,
    // Ostotositteiden komponentit
    OstotositeListausComponent,
    MaksutavatComponent,
    // OstotositeKatseleComponent,
    // OstotositeMuokkaaComponent,
    // Myyntitositteiden komponentit
    MyyntitositeListausComponent,
    // MyyntitositeMuokkaaComponent,
    // MyyntitositeKatseleComponent,
    // Palkkatositteiden komponentit
    PalkkatositeListausComponent,
    // PalkkatositeMuokkaaComponent,
    // PalkkatositeKatseleComponent,
    // Tiliotetositteiden komponentit
    TiliotetositeListausComponent,
    // TiliotetositeMuokkaaComponent,
    // TiliotetositeKatseleComponent,
    // Selvitettävien tositteiden komponentit
    SelvitettavaTositeListausComponent,
    SelvitettavatMaksutapaComponent,
    // Laskujen vastaanotto
    LaskujenVastaanottoComponent,
    LaskujenHyvaksyminenComponent,
    LaskujenHyvaksyminenEiVoiHyvaksyaEnaaDialog,
    // Ajopäiväkirja
    KirjanpidonUlkopuolisetKulutComponent,
    // Aineiston lataus
    AineistonLatausComponent,
    LemonaidPrimaryButtonComponent,
    LemonaidSecondaryButtonComponent,
    LemonaidIconButtonComponent,
    LemonaidButtonComponent,
    LemonaidMenuItemButtonComponent,
    // Pipes
    CurrencyPipe,
    CurrencyWithLocalePipe,
    BigCurrencyPipe,
    BigCurrencyWithLocalePipe,
    LemonKaannaPipe,
    LemonKaannaWithLocalePipe,
    LemonDatePipe,
    LemonDateWithLocalePipe,
    LemonLocalDatePipe,
    LemonNumberDatePipe,
    LemonNumberDateWithLocalePipe,
    LemonNumberDateAndTimePipe,
    LemonNumberDateWithLocaleNarrowPipe,
    LemonDateAndTimePipe,
    LemonLocalDateWithLocalePipe,
    LemonTimestampPipe,
    LemonTimestampWithLocalePipe,
    LemonTimestampDateAndTimePipe,
    LemonTimestampDateAndTimeWithLocalePipe,
    LemonLocalToMonthAndYearPipe,
    LemonNumberMonthToMonthAndYearPipe,
    LemonMaaPipe,
    LemonMaaWithlocalePipe,
    DecimalPipe,
    DecimalWithLocalePipe,
    BigDecimalPipe,
    BigDecimalWithLocalePipe,
    PrettyPrintJsonPipe,
    // AuthImagePipe
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    FormField,
    routing,
    // CDK below this
    A11yModule,
    CdkTableModule,
    OverlayModule,
    ScrollingModule,
    // Material below this
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatStepperModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatSelectModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatDialogModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatChipsModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatRadioModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
  ],
  providers: [
    // Sovelluksen tyylit värittävät virheelliset kentät ng-invalid- ja ng-touched-luokkien perusteella, joita Signal Forms ei muuten lisää
    provideSignalFormsConfig({ classes: NG_STATUS_CLASSES }),
    provideDateFnsAdapter({
      parse: {
        dateInput: 'P',
        timeInput: 'p',
      },
      display: {
        dateInput: 'P',
        timeInput: 'p',
        monthYearLabel: 'LLLL uuuu',
        dateA11yLabel: 'PP',
        monthYearA11yLabel: 'LLLL uuuu',
        timeOptionLabel: 'p',
      },
    }),
    // Guards
    AuthGuard,
    AsetuksetGuard,
    RoleGuard,
    RoleByTositeTypeGuard,
    ValikkoKiinniGuard,
    ValikkoAukiGuard,
    TilinpaatosUsersGuard,
    TaukoGuard,
    // Resolvers, lasku
    LaskuKatseleComponentDataResolve,
    TuoteComponentDataResolve,
    AsiakasComponentDataResolve,
    LaskuComponentDataResolve,
    LaskutComponentDataResolve,
    // Resolvers, tosite
    TositeKatseleComponentDataResolve,
    TositeMuokkaaComponentDataResolve,
    TositeLuoUusiComponentDataResolve,
    // Resolvers, maksu
    MaksuKatseleComponentDataResolve,
    MaksuMuokkaaComponentDataResolve,
    MaksuLuoUusiComponentDataResolve,
    MaksuToimittajaDataResolve,
    // Services
    // Vain webin käytössä olevat palvelut
    VersionTarkistusPalvelu,
    LoginService,
    LemonHttpService,
    KayttajaService,
    LemonTranslationService,
    MaaService,
    VasenValikkoService,
    FormValidationService,
    WindowSizeService,
    LaskuSummatService,
    LocalStorageService,
    LadataanService,
    TimestampService,
    AsetustenOletuksetService,
    PdfService,
    DragAndDropService,
    TiedostojenLataamisService,
    LaskuttavaAsiakasLemonaidProvider,
    AiiaDialogService,
    HolviDialogService,
    EnableBankingDialogService,
    TunnistautuminenService,
    AllowedDialogsService,
    TuntemistiedotDialogService,
    MuutoksiaPalveluunDialogService,
    ReportImagesService,
    TilinpaatosLemonaidUsersService,
    LemonBarcodeDetector,
    UserSettingsService,
    PalvelukokonaisuusService,
    // Palkkaus.fi
    PalkkausFiService,
    // Shared
    { provide: TrackingService, useClass: TrackingServiceLemonaidImpl },
    { provide: BicService, useClass: BicAngularServiceAngular },
    { provide: IbanService, useClass: IbanServiceAngular },
    { provide: CurrencyService, useClass: CurrencyServiceAngular },
    { provide: DateService, useClass: DateServiceAngular },
    { provide: StringService, useClass: StringServiceAngular },
    { provide: FirestoreIndeksoija, useClass: FirestoreIndeksoijaAngular },
    { provide: KopioijaPalvelu, useClass: KopioijaPalveluAngular },
    { provide: TranslationService, useClass: TranslationServiceAngular },
    { provide: ViitenumeroService, useClass: ViitenumeroServiceAngular },
    { provide: KayttoehdotUriService, useClass: KayttoehdotUriServiceAngular },
    { provide: CodeCheckService, useClass: CodeCheckServiceAngular },
    { provide: YksikkoService, useClass: YksikkoServiceAngular },
    { provide: KycUriService, useClass: KycUriServiceAngular },
    { provide: TilinpaatosUriService, useClass: TilinpaatosUriServiceAngular },
    { provide: BankConsentUriService, useClass: BankConsentUriServiceAngular },
    { provide: BankBalancePublicUriService, useClass: BankBalancePublicUriServiceAngular },
    { provide: VatNumberValidationService, useClass: VatNumberValidationServiceAngular },
    { provide: ToimittajaTunnisteService, useClass: ToimittajaTunnisteServiceAngular },
    { provide: AsiakasJaettuLemonaidService, useClass: AsiakasJaettuLemonaidServiceAngular },
    { provide: MaksutService, useClass: MaksutServiceAngular },
    { provide: LemonaidRaportitUriService, useClass: LemonaidRaportitUriServiceAngular },
    { provide: LemonaidProjektitUriService, useClass: LemonaidProjektitUriServiceAngular },
    { provide: ProjektitService, useClass: ProjektiServiceLemonaidImpl },

    // Shared, Lasku
    { provide: VahvistettuKorkoService, useClass: VahvistettuKorkoServiceAngular },
    { provide: LaskuIndeksoija, useClass: LaskuIndeksoijaAngular },
    { provide: LaskuKopioija, useClass: LaskuKopioijaAngular },
    { provide: LaskuKorkoService, useClass: LaskuKorkoServiceAngular },
    { provide: LaskuLahetyksenToistoService, useClass: LaskuLahetyksenToistoServiceAngular },
    { provide: ReskontraService, useClass: ReskontraServiceAngular },
    { provide: LaskuSharedService, useClass: LaskuSharedServiceAngular },
    { provide: LaskuSpostiService, useClass: LaskuSpostiServiceAngular },
    { provide: LaskuUriService, useClass: LaskuUriServiceAngular },
    { provide: SahkoisenLaskunValittajaService, useClass: SahkoisenLaskunValittajaServiceAngular },
    // Shared in Angular, Lasku
    KonfiguraatioPalvelu,
    AlvNumeronTarkistajaService,
    LaskunAsiakasTypeahead,
    LaskunTuoteTypeahead,
    LaskunToimittajaTypeahead,
    LaskuService,
    LaskutusprofiiliService,
    LaskutusHakuStateService,
    AsiakasDialogiService,
    TuoteDialogiService,
    LaskunAsiakkaanValidointiService,
    AlvService,
    FileSaverService,
    // Shared, Tosite
    { provide: TositeUriService, useClass: TositeUriServiceAngular },
    { provide: TositeIndeksoija, useClass: TositeIndeksoijaAngular },
    { provide: TositeKopioija, useClass: TositeKopioijaAngular },
    TositeService,
    MaksukorttiService,
    TositeKuvaService,
    TositeKuvaCacheService,
    TositeDatasourceService,
    TositeDatasourceUusiService,
    TositeHakuStateService,
    SelvitettavatTositteetService,
    EinvoicesAngularService,
    // Datasources, Lasku
    TuotteetFirestoreDataSource,
    AsiakkaatFirestoreDataSource,
    LaskutFirestoreDataSource,
    LaskuHakuDataSourceLemonaid,
    // Maksut
    MaksutSharedStateService,
    MaksutToimittajatFirestoreDataSource,
    // Datasources, Tositteet
    // OstotositeListausFirebaseDataSource,
    // MyyntitositeListausFirebaseDataSource,
    // PalkkatositeListausFirebaseDataSource,
    // TiliotetositeListausFirebaseDataSource,
    { provide: CountryLocalizationService, useExisting: MaaService },
    { provide: LaskuttavaAsiakasProvider, useExisting: LaskuttavaAsiakasLemonaidProvider },
    { provide: TimestampProviderBase, useExisting: TimestampService },
    { provide: LaskunAsiakasTypeaheadBase, useExisting: LaskunAsiakasTypeahead },
    { provide: LaskunTuoteTypeaheadBase, useExisting: LaskunTuoteTypeahead },
    { provide: LaskunToimittajaTypeaheadBase, useExisting: LaskunToimittajaTypeahead },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: DateAdapter, useClass: LemonDateAdapter },
    { provide: LemonDateAdapter, useClass: LemonDateAdapter },
    // { provide: RouteReuseStrategy, useClass: LemonRouteReuseStrategy },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    { provide: MatSortHeaderIntl, useClass: MyMaterialMatSortHeaderIntl },
    { provide: MatStepperIntl, useClass: MyMaterialMatStepperIntl },
    { provide: MatPaginatorIntl, useClass: MyMaterialPagingIntl },
    // Firebase
    FirebaseLemonaid,
    { provide: SharedFirebaseLemonaid, useExisting: FirebaseLemonaid },
    { provide: SharedFirebaseCurrentApp, useExisting: FirebaseLemonaid },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
